import React, { useRef, useState, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { LoadingSpinner, Button, Icon, mapPlaylistFromVideoSearch } from 'yankees-react-utils';
import { useOktaQuery, PlayerReport, Approach as ApproachData } from '../../../data';
import { StyledReportCard, reportCardBorder } from '../../../components/ReportCard';
import usePlayerSelector from '../../../utils/usePlayerSelector';
import ApproachVideoButton from '../../../components/ApproachVideoButton';
import VideoPlayer from '../../../components/VideoPlayer';
import ApproachModal from './ApproachModal';
import { store } from '../../../store';

const StyledTabs = styled.div`
    > div + div {
        margin: 0;
        border: ${reportCardBorder};
        border-radius: 0.25rem;
    }
`;
const Container = styled.div`
    margin: 0px 25px 0px 5px;
    > * {
        margin-bottom: 20px;
    }
    ${StyledReportCard} .card-body > h4, ${StyledTabs} > div + div > h4 {
        font-size: 18px;
        font-weight: bold;
        text-align: left;
        text-transform: uppercase;
    }
    .table {
        font-size: 12px;
    }
    .thinBlueBorder {
        border-color: #2671ce !important;
        border-width: 1px !important;
        color: #2671ce !important;
        font-weight: bold;
    }
    .whiteText {
        color: white !important;
    }
    .horiz-scroll-row > .row {
        display: block;
        overflow-x: auto;
        white-space: nowrap;
    }
`;
const StyledPitchButton = styled(Button)`
    width: 95%;
    font-size: 10px;
    && {
        font-weight: bold;
    }
`;
const InlineDiv = styled.div`
    display: inline-block;
    height: 60px;
    width: 106px;
`;

// based on https://stackoverflow.com/a/2450976
function shuffle(array) {
    let currentIndex = array.length;
    let randomIndex;

    // While there remain elements to shuffle.
    while (currentIndex !== 0) {
        // Pick a remaining element.
        randomIndex = Math.floor(Math.random() * currentIndex);
        // eslint-disable-next-line no-plusplus
        currentIndex--;

        // And swap it with the current element.
        [array[currentIndex], array[randomIndex]] = [
            array[randomIndex], array[currentIndex]];
    }

    return array;
}

const Approach = ({ isLoadingPitcher, pitcherIdRaw, pitcher, loggedInPlayerBats }) => {
    const { t: translate } = useTranslation();
    const modalContainer = useRef(null);
    const videoPlayerModalContainer = useRef();
    const { state: { user: loggedInUser } } = useContext(store);
    const userName = loggedInUser.UserName;
    const pitcherId = typeof pitcherIdRaw === 'string' ? parseInt(pitcherIdRaw) : pitcherIdRaw;
    const batter = usePlayerSelector();
    const batterId = batter?.PlayerID;
    const [savedSearches, setSavedSearches] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [pitchToUpdate, setPitchToUpdate] = useState(null);
    const [playerIsPlaying, setPlayerIsPlaying] = useState(false);
    const [playerPlaylist1, setPlayerPlaylist1] = useState(null);
    const [playerPlaylist2, setPlayerPlaylist2] = useState(null);
    const [playerLoadingPlaylist, setPlayerLoadingPlaylist] = useState(false);
    const [isPlayingRandom, setIsPlayingRandom] = useState(false);
    const [isPlayingSequence, setIsPlayingSequence] = useState(false);
    const [activeClipPlaying, setActiveClipPlaying] = useState([]);
    const [playlistToStart, setPlaylistToStart] = useState(null);
    const [inEditMode, setInEditMode] = useState(false);
    const emptyLoad = (!savedSearches || (savedSearches && savedSearches.length === 0));
    const randDisabled = (savedSearches && savedSearches.length === 0);
    const sequenceDisabled = (savedSearches && savedSearches.length <= 1);
    const playPauseDisabled = !playerPlaylist1 && !playerPlaylist2;
    const activeClipApproachPitchID = activeClipPlaying?.data?.ApproachPitchID || null;
    const isLHH = loggedInPlayerBats === 3 ? pitcher?.Throws === 1 : loggedInPlayerBats === 2;

    useEffect(() => {
        // while the name of this local storage key is in react-utils,
        // we still want to be able to delete it from here. The risk
        // that the key name changes is pretty low, anyway.
        window.localStorage.removeItem('last_viewed_angle_approach');
    }, [pitcherId]);

    const setPlayerPlaylistImp = (playlist) => {
        setPlayerLoadingPlaylist(true);
        const newPlaylist = mapPlaylistFromVideoSearch({ videos: (playlist), playervideos: [] }, loggedInUser.Roles.map((r) => r.RoleName));
        // we need to rotate between two players. This is because the player doesn't like when two
        // playlists are all the same clips, so it just ignores the re-render. In this implementation,
        // it rotates between them equally which means it always respects the replacement playlist.
        if (playerPlaylist1 && playerPlaylist1.length > 0) {
            setPlayerPlaylist1(null);
            setPlayerPlaylist2(newPlaylist);
        } else {
            setPlayerPlaylist1(newPlaylist);
            setPlayerPlaylist2(null);
        }
    };

    const playStateChanged = (state) => {
        setPlayerIsPlaying(state);
    };

    const onActiveVideoChanged = (clip) => {
        setActiveClipPlaying(clip);
        setPlayerLoadingPlaylist(false);
    };

    const videosDownloaded = (id, videos) => {
        const targetOld = savedSearches.filter((x) => x.ApproachPitchID === id)[0];
        videos.forEach((vid) => {
            vid.ApproachPitchID = id;
        });
        const targetNew = { ...targetOld, videos };
        setSavedSearches([targetNew, ...savedSearches.filter((x) => x.ApproachPitchID !== id)]);
    };

    const onPlayThisPlaylist = (id) => {
        const targetApproach = savedSearches.filter((x) => x.ApproachPitchID === id);
        if (targetApproach && targetApproach[0] && targetApproach[0].videos) {
            setPlayerPlaylistImp(targetApproach[0].videos);
            setIsPlayingRandom(false);
            setIsPlayingSequence(false);
        } else {
            console.log(`Cannot play ${id}, video length is 0`);
        }
    };

    const onEditThisPlaylist = (id) => {
        if (playerIsPlaying) {
            // pause active video for edit
            videoPlayerModalContainer.current.togglePlay();
        }
        setPitchToUpdate(savedSearches.find((pit) => pit.ApproachPitchID === id));
        setShowModal(true);
    };

    const onAddPitch = () => {
        if (playerIsPlaying) {
            // pause active video for add
            videoPlayerModalContainer.current.togglePlay();
        }
        setPitchToUpdate(null);
        setShowModal(true);
    };

    const onPlaySequencePlaylist = () => {
        const newSearch = [];

        // what we need is to weave the playlists, so let's make them
        // a consistent length, then weave them
        let maxPlaylistLength = 0;
        savedSearches.forEach((search) => {
            if (search.videos && search.videos.length > maxPlaylistLength) {
                maxPlaylistLength = search.videos.length;
            }
        });
        savedSearches.forEach((search) => {
            if (search.videos) {
                const numberToAdd = maxPlaylistLength - search.videos.length;
                const newVideos = [...search.videos];
                for (let index = 0; index < numberToAdd; index++) {
                    newVideos.push(search.videos[Math.floor(Math.random() * search.videos.length)]);
                }

                const targetOld = savedSearches.filter((x) => x.ApproachPitchID === search.ApproachPitchID)[0];
                const targetNew = { ...targetOld, videos: newVideos };
                newSearch.push(targetNew);
            }
        });

        // now we cycle over each saved search
        const seqPlaylist = [];
        for (let index = 0; index < maxPlaylistLength; index++) {
            newSearch.forEach((search) => {
                seqPlaylist.push(search.videos[index]);
            });
        }

        if (seqPlaylist && seqPlaylist.length > 0) {
            setPlayerPlaylistImp(seqPlaylist);
            setIsPlayingRandom(false);
            setIsPlayingSequence(true);
        } else {
            console.log('Cannot play Sequence, video length is 0');
        }
    };

    const onPlayRandomPlaylist = () => {
        let allVideos = [];
        savedSearches.forEach((list) => {
            allVideos = allVideos.concat(list.videos);
        });
        if (allVideos && allVideos.length > 0) {
            setPlayerPlaylistImp(shuffle(allVideos));
            setIsPlayingRandom(true);
            setIsPlayingSequence(false);
        } else {
            console.log('Cannot play Random, video length is 0');
        }
    };

    useEffect(() => {
        if (playlistToStart) {
            onPlayThisPlaylist(playlistToStart);
            setPlaylistToStart(null);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [playlistToStart, savedSearches]);

    const onSaveModal = (isDelete, savedSearch) => {
        // We need to remove anything from saved searches with this ID (delete and update cases), so that
        // we don't end up adding to the searches instead of updating one
        if (isDelete) {
            setPlayerPlaylist1(null);
            setPlayerPlaylist2(null);
            setActiveClipPlaying(null);
            setSavedSearches([...savedSearches.filter((x) => x.ApproachPitchID !== savedSearch.ApproachPitchID)]);
        } else {
            savedSearch.videos.forEach((vid) => {
                vid.ApproachPitchID = savedSearch.ApproachPitchID;
            });
            setSavedSearches([savedSearch, ...savedSearches.filter((x) => x.ApproachPitchID !== savedSearch.ApproachPitchID)]);
            setPlaylistToStart(savedSearch?.ApproachPitchID);
        }

        setPitchToUpdate(null);
        setShowModal(false);
        setInEditMode(false);
    };

    const { data: pitcherRepertoire, isFetching: pitcherRepertoireLoading } = useOktaQuery(['pitcherRepertoire', { playerId: pitcherId }], (t) => PlayerReport.pitchcharacteristics(t, { playerId: pitcherId }, { enabled: pitcherId }));
    const { isFetching: savedSearchesLoading } = useOktaQuery(['userSavedSearches', { pitcherId, batterId, userName }],
        (t) => ApproachData.getsaved(t, { pitcherId, batterId, userName }),
        { enabled: pitcherId && batterId != null,
            onSuccess: (data) => {
                setSavedSearches([...data]);
                if (!data.length) {
                    setShowModal(true);
                }
            } });

    useEffect(() => {
        setPlayerPlaylist1(null);
        setPlayerPlaylist2(null);
    }, [pitcherId]);

    const hasPitchRepertoire = pitcherRepertoire && pitcherRepertoire.pitchtypes && pitcherRepertoire.pitchtypes.length > 0;
    const fxPitchTypes = hasPitchRepertoire && pitcherRepertoire.pitchtypes.map((x) => x.pitchtype);

    if (isLoadingPitcher || pitcherRepertoireLoading || savedSearchesLoading) {
        return <div style={{ margin: '10px' }}><LoadingSpinner /></div>;
    }

    return (
        <Container ref={modalContainer}>
            <Row>
                <Col md={9} className="horiz-scroll-row">
                    <div style={{ paddingLeft: '12px', paddingRight: '3px', paddingTop: '3px' }} className="g-0">
                        {savedSearches && savedSearches.sort((a, b) => (a.ApproachPitchID < b.ApproachPitchID ? 1 : -1)).map((p) => (
                            <InlineDiv key={p.ApproachPitchID}>
                                <ApproachVideoButton
                                    approachPitch={p}
                                    isPlaying={activeClipApproachPitchID === p.ApproachPitchID}
                                    isEditMode={inEditMode}
                                    onVideosDownloaded={videosDownloaded}
                                    onPlayThisPlaylist={onPlayThisPlaylist}
                                    onEditThisPlaylist={onEditThisPlaylist}
                                    pitcherId={pitcherId}
                                    batterId={batterId}
                                />
                            </InlineDiv>
                        ))}
                        <div className="btn-group-vertical" style={{ paddingLeft: '0', paddingRight: '10px', paddingTop: '6px', paddingBottom: '6px', height: '60px', width: '75px' }}>
                            {pitcherRepertoire && <StyledPitchButton variant="default" onClick={() => onAddPitch()} className="thinBlueBorder">+ {translate('common.add')}</StyledPitchButton>}
                            {!emptyLoad && (
                                <StyledPitchButton variant={inEditMode ? 'primary' : 'default'} onClick={() => setInEditMode(!inEditMode)} disabled={randDisabled} className={inEditMode ? 'thinBlueBorder whiteText' : 'thinBlueBorder'} style={{ marginTop: '5px' }}>
                                    {inEditMode ? translate('common.cancel') : translate('common.edit')}
                                </StyledPitchButton>
                            )}
                        </div>
                    </div>
                </Col>
                {!emptyLoad && (
                    <Col md={3} style={{ boxShadow: 'rgb(0 0 0 / 50%) -10px 0px 4px -4px' }}>
                        <Row style={{ paddingTop: '5px' }}>
                            <Col md={9} style={{ paddingRight: '0px' }}>
                                <StyledPitchButton style={{ fontSize: '10px' }} variant={isPlayingRandom ? 'primary' : 'default'} onClick={() => onPlayRandomPlaylist()} disabled={randDisabled}><Icon size="1.5em" icon="shuffle" color={isPlayingRandom ? '#FFF' : '#000'} />&nbsp;{translate('sections.opponent.player.random')}</StyledPitchButton><br />
                                <StyledPitchButton style={{ marginTop: '10px', fontSize: '10px' }} variant={isPlayingSequence ? 'primary' : 'default'} onClick={() => onPlaySequencePlaylist()} disabled={sequenceDisabled}><Icon size="1.5em" icon="loop1" color={isPlayingSequence ? '#FFF' : '#000'} />&nbsp;{translate('sections.opponent.player.sequence')}</StyledPitchButton>
                            </Col>
                            <Col md={3}>
                                <StyledPitchButton style={{ height: '100%', width: '100%' }} variant="primary" onClick={() => videoPlayerModalContainer.current.togglePlay()} disabled={playPauseDisabled}>
                                    {playerIsPlaying ? <Icon icon="pause" color="#FFF" /> : <Icon icon="play" color="#FFF" />}
                                </StyledPitchButton>
                            </Col>
                        </Row>
                    </Col>
                )}
                {emptyLoad && <Col md={3}>&nbsp;</Col>}
            </Row>
            <Row>
                <Col>
                    <div>
                        {playPauseDisabled && !emptyLoad && translate('sections.opponent.player.please-select-something')}
                        {emptyLoad && translate('sections.opponent.player.please-tap-add')}
                        {playerLoadingPlaylist && <><LoadingSpinner />&nbsp;{translate('sections.opponent.player.buffering-video')}</>}
                        {!playPauseDisabled && playerPlaylist1 && (
                            <VideoPlayer
                                ref={videoPlayerModalContainer}
                                playlist={playerPlaylist1}
                                playStateChanged={playStateChanged}
                                onActiveVideoChanged={onActiveVideoChanged}
                                specialtyAngleKey={isLHH ? 'approach-lhh' : 'approach-rhh'}
                            />
                        )}
                        {!playPauseDisabled && playerPlaylist2 && (
                            <VideoPlayer
                                ref={videoPlayerModalContainer}
                                playlist={playerPlaylist2}
                                playStateChanged={playStateChanged}
                                onActiveVideoChanged={onActiveVideoChanged}
                                specialtyAngleKey={isLHH ? 'approach-lhh' : 'approach-rhh'}
                            />
                        )}
                    </div>
                </Col>
            </Row>
            {pitcherRepertoire && (
                <ApproachModal
                    show={showModal}
                    container={modalContainer}
                    onSave={(isDelete, dbObject) => onSaveModal(isDelete, dbObject)}
                    onHide={() => { setShowModal(false); setInEditMode(false); }}
                    batterId={batterId}
                    pitcherId={pitcherId}
                    userName={userName}
                    fxpitchTypes={fxPitchTypes}
                    pitchToUpdate={pitchToUpdate}
                />
            )}
            {!pitcherRepertoire && translate('sections.opponent.player.no-repertoire')}
        </Container>
    );
};

Approach.propTypes = {
    pitcherIdRaw: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    isLoadingPitcher: PropTypes.bool,
    loggedInPlayerBats: PropTypes.number,
    // eslint-disable-next-line react/forbid-prop-types
    pitcher: PropTypes.any,
};

Approach.defaultProps = {
    isLoadingPitcher: false,
    loggedInPlayerBats: null,
    pitcher: null,
};

export default Approach;
